import { useEffect, useState } from "react";
import { ShareButton } from "@/components/ShareButton";
import { getBlogReactions, reactToBlogPost } from "@/lib/blog-reactions.functions";

interface Counts {
  likes: number;
  dislikes: number;
  mine: number;
}

/** Stable per-browser voter token so anonymous readers get one vote each. */
function voterToken(): string {
  const key = "palstars-voter";
  let token = localStorage.getItem(key);
  if (!token || token.length < 8) {
    token = `v_${crypto.randomUUID()}`;
    localStorage.setItem(key, token);
  }
  return token;
}

/**
 * YouTube-style like / dislike bar plus share targets for a published article.
 * Comments are intentionally disabled site-wide, so this is the only reader
 * feedback surface. Votes go through validated server functions that accept
 * one vote per browser token and only on published posts.
 */
export function BlogReactions({ postId, title }: { postId: string; title: string }) {
  const [counts, setCounts] = useState<Counts>({ likes: 0, dislikes: 0, mine: 0 });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const data = await getBlogReactions({ data: { postId, voter: voterToken() } });
        if (alive && data) setCounts(data);
      } catch {
        // Reader feedback is optional — never break the article on failure.
      }
    })();
    return () => {
      alive = false;
    };
  }, [postId]);

  async function vote(value: 1 | -1) {
    if (busy) return;
    setBusy(true);
    const next = counts.mine === value ? 0 : value;
    try {
      const data = await reactToBlogPost({ data: { postId, voter: voterToken(), value: next } });
      if (data) setCounts(data);
    } catch {
      // Ignore — the visible counts simply stay as they were.
    } finally {
      setBusy(false);
    }
  }

  const pill = "flex items-center gap-2 rounded-full border border-border/60 px-4 py-2 text-sm font-semibold transition";

  return (
    <section className="flex flex-wrap items-center gap-3 border-y border-border/60 py-4">
      <button
        type="button"
        onClick={() => void vote(1)}
        aria-pressed={counts.mine === 1}
        aria-label="Like this article"
        className={`${pill} ${counts.mine === 1 ? "bg-primary text-primary-foreground" : "hover:bg-card"}`}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
          <path d="M2 21h3V9H2v12Zm19.5-9.5c0-1.1-.9-2-2-2h-5.6l.8-4.1v-.3c0-.4-.2-.8-.4-1.1L13.2 3 7.6 8.6c-.4.4-.6.9-.6 1.4v9c0 1.1.9 2 2 2h8.1c.8 0 1.5-.5 1.8-1.2l2.5-5.9c.1-.2.1-.4.1-.6v-1.8Z" />
        </svg>
        {counts.likes}
      </button>
      <button
        type="button"
        onClick={() => void vote(-1)}
        aria-pressed={counts.mine === -1}
        aria-label="Dislike this article"
        className={`${pill} ${counts.mine === -1 ? "bg-destructive text-white" : "hover:bg-card"}`}
      >
        <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true" fill="currentColor">
          <path d="M22 3h-3v12h3V3ZM2.5 12.5c0 1.1.9 2 2 2h5.6l-.8 4.1v.3c0 .4.2.8.4 1.1l.9.9 5.6-5.6c.4-.4.6-.9.6-1.4V5c0-1.1-.9-2-2-2H6.3c-.8 0-1.5.5-1.8 1.2L2 10.1c-.1.2-.1.4-.1.6v1.8Z" />
        </svg>
        {counts.dislikes}
      </button>
      <ShareButton title={title} text={`${title} — Pal Stars`} />
      <span className="text-xs text-muted-foreground">Comments are off on Pal Stars articles.</span>
    </section>
  );
}
